// SharedListApi service - based on Angular sharedListApi.factory.js
// Handles all shared list operations: get lists, create list, add to list

import environmentHelper from "./environmentHelper";
import appStatePersistenceService from "./appStatePersistenceService";

class SharedListApiService {
  constructor() {
    // Get configuration from environment helper (like Angular)
    this.shared_list_endpoint = environmentHelper.getValue(
      "shared_list_endpoint"
    );
    this.shared_list_detail_url = environmentHelper.getValue(
      "shared_list_detail_url"
    );
    this.user_type_endpoint = environmentHelper.getValue("user_type_endpoint");
    this.sso_url = environmentHelper.getValue("sso_url");
    this.catalog_domain = environmentHelper.getValue("catalog_domain");

    if (!this.shared_list_endpoint) {
      throw new Error(
        "'shared_list_endpoint' is not available in the environment"
      );
    }

    if (!this.user_type_endpoint) {
      console.warn("'user_type_endpoint' is not available in the environment");
    }

    this.user_id = null; // null = not yet checked (matches Angular exactly)
  }

  async getLists() {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        // Redirect to login if not authenticated
        this.redirectToLogin();
        return [];
      }

      const response = await fetch(
        `${this.shared_list_endpoint}getLists/${userId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        console.error("Failed to fetch lists:", response.status);
        return [];
      }

      const data = await response.json();
      return data.sharedLists ? data.sharedLists.map(this.decodeList) : [];
    } catch (error) {
      console.error("Error fetching lists:", error);
      return [];
    }
  }

  async createList(name) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        this.redirectToLogin();
        return null;
      }

      const response = await fetch(this.shared_list_endpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          description: "",
          listName: name,
        }),
      });

      if (!response.ok) {
        console.error("Failed to create list:", response.status);
        return null;
      }

      const data = await response.json();
      return data.sharedList ? this.decodeList(data.sharedList) : null;
    } catch (error) {
      console.error("Error creating list:", error);
      return null;
    }
  }

  async addToList(listId, product, qty = 1) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        this.redirectToLogin();
        return {
          message_key: "login_required",
          items: [],
        };
      }

      // Match Angular master branch payload format exactly
      const payload = [
        {
          src: "CMGT-PDP",
          itemId: product.sku || product.id,
          qty: qty,
        },
      ];

      const url = `${this.shared_list_endpoint}${listId}/items`;

      // Use Angular master branch endpoint: {shared_list_endpoint}{list_id}/items
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to add to list:", {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
        return {
          message_key: "api_error",
          items: [],
        };
      }

      const data = await response.json();

      // Check for TaqMan UI error response format
      if (this.isAddToListError(data)) {
        console.error("Add to list error:", data);
        return {
          message_key: data.translationCode?.toLowerCase() || "api_error",
          items: [],
        };
      }

      if (data.sharedListItems) {
        const items = data.sharedListItems.map(this.decodeListItem);
        return {
          message_key:
            data.responseMetaData?.translationCode?.toLowerCase() ||
            "success_ok",
          items: items,
        };
      } else {
        console.error("No item created", data);
        return {
          message_key: "not_added",
          items: [],
        };
      }
    } catch (error) {
      console.error("Error adding to list:", error);
      return {
        message_key: "api_error",
        items: [],
      };
    }
  }

  getListUrl(list) {
    return `${this.shared_list_detail_url}${list.listId}`;
  }

  // Authentication methods - exactly matching Angular loginService
  async getUserId() {
    // Match Angular exactly: only fetch if user_id == null (initial state)
    // Once set to a value (even null after check), it's cached
    if (this.user_id !== null) {
      console.log("[Auth] Returning cached user ID:", this.user_id);
      return this.user_id;
    }

    if (!this.user_type_endpoint) {
      throw new Error(
        "'user_type_endpoint' is not available in the environment"
      );
    }

    console.log(
      "[Auth] Fetching fresh authentication at:",
      this.user_type_endpoint
    );
    try {
      const response = await fetch(this.user_type_endpoint, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      if (!response.ok) {
        console.error(
          "Failed to fetch user info:",
          response.status,
          response.statusText
        );
        // Don't cache - leave as null so it stays as "not checked"
        // Angular returns null but doesn't modify user_id on error
        return null;
      }

      const data = await response.json();
      console.log("[Auth] User type API response:", data);

      // Handle different response formats
      let user = data.data || data;

      // Match Angular logic exactly: if authenticated, set user_id, otherwise leave as null
      if (user && !user.isAnonymous && user.userId) {
        this.user_id = user.userId;
        console.log("[Auth] User authenticated with ID:", this.user_id);
      }
      // If not authenticated, user_id stays null (not checked yet)
      // This means next call will check again

      return this.user_id;
    } catch (error) {
      console.error("Error fetching user ID:", error);
      // Don't cache errors - return null but don't modify this.user_id
      return null;
    }
  }

  // Redirect to login page - matching TaqMan UI's simpler approach
  redirectToLogin() {
    console.log("[Auth] Redirecting to login, saving current state...");

    // Save current state before redirecting
    appStatePersistenceService
      .saveCurrentAppState()
      .then(() => {
        // Use TaqMan UI's pattern: simple URL redirect with returnUrl query parameter
        // This avoids auto-login issues and works more reliably
        const returnUrl = encodeURIComponent(window.location.href);

        // Try to get login URL from Magellan config (like TaqMan UI does)
        let loginUrl = "";
        if (window.$m && window.$m.magellanConfig) {
          const config = window.$m.magellanConfig;
          if (config.comergentHost && config.comergentBasePath) {
            loginUrl =
              "https://" +
              config.comergentHost +
              config.comergentBasePath +
              "LoginRequired";
            console.log("[Auth] Using Magellan config login URL:", loginUrl);
          }
        }

        // Fallback to default auth/login endpoint
        if (!loginUrl) {
          loginUrl = "/auth/login";
          console.log("[Auth] Using fallback login URL:", loginUrl);
        }

        // Redirect with returnUrl parameter (matching TaqMan UI)
        const finalUrl = loginUrl + "?returnUrl=" + returnUrl;
        console.log("[Auth] Redirecting to:", finalUrl);
        window.location.href = finalUrl;
      })
      .catch((error) => {
        console.error("Error saving app state before login redirect:", error);
        // Redirect anyway even if state saving fails
        const returnUrl = encodeURIComponent(window.location.href);

        let loginUrl = "/auth/login";
        if (window.$m && window.$m.magellanConfig) {
          const config = window.$m.magellanConfig;
          if (config.comergentHost && config.comergentBasePath) {
            loginUrl =
              "https://" +
              config.comergentHost +
              config.comergentBasePath +
              "LoginRequired";
          }
        }

        window.location.href = loginUrl + "?returnUrl=" + returnUrl;
      });
  }

  // Helper method to POST to URL (from Angular loginService)
  postToUrl(path, params, method = "post") {
    console.log(
      "[Auth] Creating form to POST to:",
      path,
      "with params:",
      params
    );
    const form = document.createElement("form");
    form.setAttribute("method", method);
    form.setAttribute("action", path);

    for (const key in params) {
      const hiddenField = document.createElement("input");
      hiddenField.setAttribute("type", "hidden");
      hiddenField.setAttribute("name", key);
      hiddenField.setAttribute("value", params[key]);
      form.appendChild(hiddenField);
    }

    document.body.appendChild(form);
    form.submit();
  }

  // Check if user is logged in
  isLoggedIn() {
    return this.user_id !== null;
  }

  // Error detection method from TaqMan UI
  isAddToListError(response) {
    return (
      response &&
      "code" in response &&
      "message" in response &&
      "processingTime" in response &&
      "translationCode" in response
    );
  }

  // Helper methods
  decodeList(data) {
    return data;
  }

  decodeListItem(data) {
    return data;
  }
}

// Create singleton instance
const sharedListApi = new SharedListApiService();

export default sharedListApi;
